import { DoughnutChart } from '@/ui/charts/doughnut-chart';
import { colorBlueDark } from '@/utils/helpers/css-variables.helper';
import { Skeleton } from 'primereact/skeleton';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const StyledKeyResultCard = styled.div`
  border: 1px solid var(--gray-dark);
  border-radius: 9px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: var(--big-padding);
  background-color: var(--white-main);

  .card-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--medium-padding);
  }

  .card-title {
    font-weight: 600;
    font-size: var(--semi-big-font-size);
    line-height: var(--semi-big-line-height);
    margin: 0;
  }

  .card-rows {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .card-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:not(:last-child) {
      padding-bottom: 9px;
      border-bottom: 1px solid var(--gray-dark);
    }
  }

  .card-row-label {
    font-weight: 400;
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    color: var(--black-main);

    &.card-row-link {
      text-decoration: none;

      &:hover {
        color: var(--red-main);
      }
    }
  }

  .card-row-value {
    font-weight: 600;
    font-size: var(--semi-big-font-size);
    line-height: var(--semi-big-line-height);
    color: var(--black-main);
  }
`;

export type KeyResultRowPage = {
  to: string;
  filters?: Record<string, string>;
  isDashboardLink?: boolean;
};

export type KeyResultRow = {
  label: string;
  value: number;
  page?: KeyResultRowPage;
};

export type KeyResultCardData = {
  title: string;
  tooltip?: string;
  rows: KeyResultRow[];
  doughnutNumerator: number;
  doughnutDenominator: number;
  chartId: string;
};

type KeyResultCardProps = KeyResultCardData & {
  isLoading: boolean;
};

export const KeyResultCard: React.FC<KeyResultCardProps> = ({
  title,
  tooltip,
  rows,
  doughnutNumerator,
  doughnutDenominator,
  chartId,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <StyledKeyResultCard>
        <div className="card-info">
          <Skeleton width="50%" height="22px" />
          <div className="card-rows">
            <div className="card-row">
              <Skeleton width="40%" height="18px" />
              <Skeleton width="10%" height="22px" />
            </div>
            <div className="card-row">
              <Skeleton width="40%" height="18px" />
              <Skeleton width="10%" height="22px" />
            </div>
          </div>
        </div>
        <Skeleton shape="circle" size="109px" className="flex-shrink-0" />
      </StyledKeyResultCard>
    );
  }

  return (
    <StyledKeyResultCard>
      <div className="card-info">
        <h3 className="card-title">{title}</h3>
        <div className="card-rows">
          {rows.map((row, index) => (
            <div className="card-row" key={`${chartId}-${index}`}>
              {row.page ? (
                <Link
                  to={{
                    pathname: row.page.to,
                    search: row.page.filters
                      ? `filters=${encodeURIComponent(
                          JSON.stringify(row.page.filters),
                        )}`
                      : undefined,
                  }}
                  state={{ isDashboardLink: row.page.isDashboardLink }}
                  className="card-row-label card-row-link"
                >
                  {row.label}
                </Link>
              ) : (
                <span className="card-row-label">{row.label}</span>
              )}
              <span className="card-row-value">{row.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div id={`chart-tooltip-${chartId}`}>
        <DoughnutChart
          allCount={doughnutDenominator}
          singleCount={doughnutNumerator}
          id={chartId}
          cutout="70%"
          width="109px"
          isPercentCount
          isDashboard
          mainColor={colorBlueDark(getComputedStyle(document.documentElement))}
        />
        {tooltip && (
          <Tooltip
            target={`#chart-tooltip-${chartId}`}
            content={tooltip}
            position="top"
          />
        )}
      </div>
    </StyledKeyResultCard>
  );
};
